import React, { useState } from "react";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { certifyProgram, getCertifications, getProgramsVerificationPoints } from "services";

import { formatDayDate, formatNumber, getProgramInternalName, useGetAllCarbonPrograms } from "utils";

import { GET_CERTIFICATIONS, GET_PROGRAM_VERIFICATION_POINTS } from "consts";

import { Certification, ProgramVerificationPoints } from "models";

import { Button, Col, Flex, Icon, Modal, Row, Table, TooltipWrapper, Typography, Message as message } from "components";

type CertificationYearTabProps = {
  year: number;
};

export const CertificationYearTab = ({ year }: CertificationYearTabProps) => {
  const queryClient = useQueryClient();
  const [certifyProgramId, setCertifyProgramId] = useState<string | null>(null);
  const { data: carbonPrograms = [] } = useGetAllCarbonPrograms();

  const { data: certifications = [], isLoading: isCertificationsLoading } = useQuery<Certification[]>({
    queryKey: [GET_CERTIFICATIONS, year],
    queryFn: () => getCertifications({ year }),
  });

  const certifiedProgramIds = new Set(certifications.map((c) => c.programId));
  const pendingPrograms = carbonPrograms
    .filter((p) => !certifiedProgramIds.has(p.id))
    .sort((a, b) => getProgramInternalName(a).localeCompare(getProgramInternalName(b)));

  const programIds = carbonPrograms.map((p) => p.id);
  const { data: verificationPoints = [], isLoading: isPointsLoading } = useQuery<ProgramVerificationPoints[]>({
    queryKey: [GET_PROGRAM_VERIFICATION_POINTS, programIds, year],
    queryFn: () => getProgramsVerificationPoints({ programIds, year }),
    enabled: programIds.length > 0,
  });

  const verificationPointsByProgramId = new Map(verificationPoints.map((vp) => [vp.programId, vp]));
  const carbonProgramsById = new Map(carbonPrograms.map((p) => [p.id, p]));

  const { mutate: handleCertify, isPending: isCertifying } = useMutation({
    mutationFn: certifyProgram,
    onSuccess: () => {
      setCertifyProgramId(null);
      message({ content: "Program certified successfully", icon: "gremlin" });
      queryClient.invalidateQueries({ queryKey: [GET_CERTIFICATIONS] });
      queryClient.invalidateQueries({ queryKey: [GET_PROGRAM_VERIFICATION_POINTS] });
    },
    onError: () => {
      message({ content: "Failed to certify program", icon: "gremlin" });
    },
  });

  const pendingTableData = pendingPrograms.map((program) => {
    const pointsData = verificationPointsByProgramId.get(program.id);
    return {
      key: program.id,
      programName: getProgramInternalName(program),
      initialCredits: pointsData ? formatNumber({ number: pointsData.initialPoints / 1000 }) : "-",
      payoutApprovedCredits: pointsData ? formatNumber({ number: pointsData.payoutApprovedPoints / 1000 }) : "-",
      currentCredits: pointsData ? formatNumber({ number: pointsData.currentPoints / 1000 }) : "-",
      certify: program.id,
    };
  });

  const pendingColumns = [
    { title: "Program Name", dataIndex: "programName", key: "programName" },
    { title: "Initial Credits", dataIndex: "initialCredits", key: "initialCredits" },
    { title: "Payout Approved Credits", dataIndex: "payoutApprovedCredits", key: "payoutApprovedCredits" },
    {
      title: (
        <Flex gap={4} align="center">
          <Typography variant="paragraph-xs">Credits to certify</Typography>
          {year === 2025 && (
            <TooltipWrapper
              tooltip={{
                title:
                  "To match credits sent to TÜV insetting programs only take into account credits before April 3rd and some offsetting credits are overidden",
                placement: "top",
              }}
            >
              <Icon iconName="tabler/info-circle" size="xs" className="grey-text pointer" />
            </TooltipWrapper>
          )}
        </Flex>
      ),
      dataIndex: "currentCredits",
      key: "currentCredits",
    },
    {
      title: "Action",
      dataIndex: "certify",
      key: "certify",
      render: (programId: string) => (
        <Button type="primary" size="small" onClick={() => setCertifyProgramId(programId)}>
          Certify
        </Button>
      ),
    },
  ];

  const getCertificationProgramName = (cert: Certification) => {
    const program = carbonProgramsById.get(cert.programId);
    return program ? getProgramInternalName(program) : cert.programName ?? cert.programId;
  };

  const certifiedTableData = [...certifications]
    .sort((a, b) => getCertificationProgramName(a).localeCompare(getCertificationProgramName(b)))
    .map((cert) => {
      const pointsData = verificationPointsByProgramId.get(cert.programId);
      return {
        key: cert.id,
        programName: getCertificationProgramName(cert),
        certifiedCredits: pointsData ? formatNumber({ number: pointsData.certifiedPoints / 1000 }) : "-",
        certifiedAt: formatDayDate(cert.createdAt),
        certifiedBy: cert.adminName,
      };
    });

  const certifiedColumns = [
    { title: "Program Name", dataIndex: "programName", key: "programName" },
    { title: "Certified Credits", dataIndex: "certifiedCredits", key: "certifiedCredits" },
    { title: "Date of Certification", dataIndex: "certifiedAt", key: "certifiedAt" },
    { title: "Certified By", dataIndex: "certifiedBy", key: "certifiedBy" },
  ];

  const isLoading = isCertificationsLoading || isPointsLoading;
  const programToCertify = carbonPrograms.find((p) => p.id === certifyProgramId);

  return (
    <>
      <Row className="margin-top-100">
        <Col>
          <Typography variant="h2" className="margin-bottom-200">
            Pending Certifications
          </Typography>
          <Table data={pendingTableData} columns={pendingColumns} loading={isLoading} pagination={false} />
        </Col>
      </Row>

      <Row>
        <Col>
          <Typography variant="h2" className="margin-top-400 margin-bottom-200">
            Certified Programs
          </Typography>
          <Table data={certifiedTableData} columns={certifiedColumns} loading={isCertificationsLoading} pagination={false} />
        </Col>
      </Row>

      <Modal
        showModal={!!certifyProgramId}
        onCancel={() => setCertifyProgramId(null)}
        onOk={() => certifyProgramId && handleCertify({ programId: certifyProgramId, year })}
        okText="Certify"
        title={`Certify ${programToCertify ? getProgramInternalName(programToCertify) : "program"}`}
        description="Are you sure you want to certify this program?"
        loading={isCertifying}
      />
    </>
  );
};
